/** The least a word must expose for its timing to be computed against: its spoken span. */
export type TimedWord = { startTime: number; endTime: number };

/**
 * The midpoint of the silence between two adjacent spoken words — our convention for where a
 * boundary between words falls (cuts, overlay edges, scene splits), so that neither word's audio
 * is clipped by the boundary. The backend clipper (openai_gpt/transcript_clipper.py) follows the
 * same rule.
 */
export function wordGapMidpoint(prevWord: TimedWord, nextWord: TimedWord): number {
    return (prevWord.endTime + nextWord.startTime) / 2;
}

/**
 * Convert a span of transcript words (e.g. a findWordSequenceIndices match) into a clip region.
 * Each boundary falls on the midpoint of the silence between the span and its neighboring word;
 * where the span starts or ends the transcript, the word's own time is used.
 */
export function wordSpanToClipRegion(
    words: readonly TimedWord[],
    span: { startIndex: number; endIndex: number },
): { startTimeMs: number; endTimeMs: number } {
    const firstWord = words[span.startIndex];
    const lastWord = words[span.endIndex];
    const wordBefore = words[span.startIndex - 1];
    const wordAfter = words[span.endIndex + 1];
    return {
        startTimeMs: wordBefore ? wordGapMidpoint(wordBefore, firstWord) : firstWord.startTime,
        endTimeMs: wordAfter ? wordGapMidpoint(lastWord, wordAfter) : lastWord.endTime,
    };
}
